import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * Feature gate wrapper. Shows blurred overlay with upgrade CTA when feature is locked.
 * Usage: <FeatureGate feature="content_generation" tier={userTier}>...content...</FeatureGate>
 */
export function FeatureGate({ feature, tier = 'free', children, blurAmount = 6 }) {
  const navigate = useNavigate();

  const FEATURE_ACCESS = {
    free: ['scan_once', 'profile_page'],
    starter: ['scan_once', 'profile_page', 'weekly_scan', 'content_generation',
              'review_solicitation', 'review_responses', 'email_digest', 'wins_feed'],
    professional: ['scan_once', 'profile_page', 'weekly_scan', 'content_generation',
                   'review_solicitation', 'review_responses', 'email_digest', 'wins_feed',
                   'competitor_tracking', 'lead_capture', 'credential_verification',
                   'multi_profession', 'advanced_analytics', 'priority_content'],
  };

  const features = FEATURE_ACCESS[tier] || FEATURE_ACCESS.free;
  const isLocked = !features.includes(feature);

  if (!isLocked) return children;

  // Determine which plan unlocks
  const unlockPlan = FEATURE_ACCESS.starter.includes(feature) ? 'Starter' : 'Professional';

  return (
    <div className="relative" data-testid={`feature-gate-${feature}`}>
      <div style={{ filter: `blur(${blurAmount}px)`, pointerEvents: 'none' }}>
        {children}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[2px] rounded-sm z-10">
        <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: 'rgba(198,122,92,0.08)' }}>
          <Lock size={20} strokeWidth={1.5} style={{ color: '#C67A5C' }} />
        </div>
        <p className="text-sm font-medium mb-1" style={{ color: '#3C3226' }}>{unlockPlan} feature</p>
        <p className="text-xs mb-4 text-center px-6" style={{ color: '#8C7E6E' }}>Upgrade to unlock</p>
        <button
          onClick={() => navigate('/pricing')}
          className="flex items-center gap-1.5 text-[#F0EBE3] text-sm font-medium px-5 py-2.5 rounded-none transition-all active:scale-[0.97]"
          style={{ backgroundColor: '#2C2520' }}
          data-testid={`upgrade-gate-${feature}`}
        >
          <Sparkles size={14} /> Upgrade
        </button>
      </div>
    </div>
  );
}

/**
 * Post-scan upgrade prompt shown after free scan completes.
 */
export function PostScanUpgradePrompt({ score, onDismiss }) {
  const navigate = useNavigate();

  return (
    <div className="p-6 rounded-sm text-[#F0EBE3]" style={{ backgroundColor: '#2C2520' }} data-testid="post-scan-upgrade">
      <div className="text-center mb-5">
        <p className="v4-eyebrow mb-2" style={{ color: '#C67A5C' }}>Your scan is complete</p>
        <div className="text-5xl font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{score}<span className="text-2xl font-normal text-[#F0EBE3]/50">/100</span></div>
        <p className="text-sm text-[#F0EBE3]/60">Here's what a paid plan unlocks for you:</p>
      </div>

      <div className="space-y-2.5 mb-6">
        {[
          'Weekly AI re-scans to track your progress',
          'Blog posts + social content written for you',
          'Send review requests via text message',
          'AI-drafted responses to customer reviews',
          'Monday morning email digest',
        ].map((item) => (
          <div key={item} className="flex items-center gap-2.5">
            <Sparkles size={13} style={{ color: '#C67A5C' }} className="flex-shrink-0" />
            <span className="text-sm text-[#F0EBE3]/80">{item}</span>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <button
          onClick={() => navigate('/pricing')}
          className="w-full py-3.5 rounded-none text-[#F0EBE3] text-sm font-medium transition-all active:scale-[0.98]"
          style={{ backgroundColor: '#C67A5C' }}
          data-testid="post-scan-upgrade-btn"
        >
          See pricing — starts at $29/mo
        </button>
        <button
          onClick={onDismiss}
          className="w-full py-3 rounded-none text-[#F0EBE3]/40 hover:text-[#F0EBE3]/60 text-sm transition-colors"
          data-testid="post-scan-skip-btn"
        >
          Continue with free plan
        </button>
      </div>
    </div>
  );
}
